// Thin fetch wrappers for the Key Receipt dashboard API.
// All requests send cookies so the worker can resolve the current user.

export type IntegrationType = 'claude_code' | 'openai' | 'anthropic_api';

export type PricingMode = 'api_key' | 'subscription';

export type OutboundSlot = {
  slot: number;
  label: string;
  pricingMode: PricingMode;
  monthlyFeeCents: number;
  configured: boolean;
  preview: string | null;
  createdAt: string;
};

export type OutboundEntry = {
  id: IntegrationType;
  label: string;
  outboundLabel: string;
  outboundPlaceholder: string;
  outboundHint: string;
  slots: OutboundSlot[];
};

export type UsageSnapshot = {
  requests: number;
  inputTokens: number;
  outputTokens: number;
  cacheCreationInputTokens: number;
  cacheReadInputTokens: number;
  lastRequestAt: string | null;
  costMicros: number;
  costMode: PricingMode;
  subscriptionShare: { share: number; monthlyFeeCents: number } | null;
};

export type InboundEntry = {
  id: string;
  tokenPreview: string;
  label: string;
  type: IntegrationType;
  typeLabel: string;
  outboundSlot: number;
  createdAt: string;
  revokedAt: string | null;
  pausedAt: string | null;
  usage: UsageSnapshot | null;
};

export type ManagementKey = {
  id: string;
  label: string;
  tokenPreview: string;
  createdAt: string;
  revokedAt: string | null;
  lastUsedAt: string | null;
};

export type ManagementKeyCreated = {
  id: string;
  token: string;
  label: string;
  createdAt: string;
};

export type InstallSnippet = {
  title: string;
  language: string;
  snippet: string;
};

export type InstallBundle = {
  baseUrl: string;
  envVars: { name: string; value: string }[];
  quickStart: InstallSnippet;
  profileInstall: InstallSnippet;
};

export type InboundCreated = {
  id: string;
  token: string;
  label: string;
  type: IntegrationType;
  typeLabel: string;
  outboundSlot: number;
  createdAt: string;
  install: InstallBundle;
};

export type IntegrationTypeMeta = {
  id: IntegrationType;
  label: string;
  blurb: string;
};

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  });
  if (response.status === 401) {
    throw new ApiError(401, 'Unauthorized');
  }
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body?.error) message = body.error;
    } catch {
      // leave default message
    }
    throw new ApiError(response.status, message);
  }
  return (await response.json()) as T;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export type CurrentUser = {
  email: string;
  name: string | null;
  picture: string | null;
  plan: 'solo' | 'agency' | 'canceled' | null;
  subscriptionStatus:
    | 'active'
    | 'trialing'
    | 'canceling'
    | 'canceled'
    | 'past_due'
    | null;
  currentPeriodEnd: number | null;
  trialEnd: number | null;
  stripeCustomerId: string | null;
  hasActiveSubscription: boolean;
};

export async function fetchMe(): Promise<CurrentUser> {
  return request('/_auth/me');
}

export async function createStripeCheckout(
  plan: 'solo' | 'agency',
): Promise<{ url: string }> {
  return request('/_stripe/checkout', {
    method: 'POST',
    body: JSON.stringify({ plan }),
  });
}

export async function createStripePortal(): Promise<{ url: string }> {
  return request('/_stripe/portal', { method: 'POST' });
}

export async function listOutbound(): Promise<{ outbound: OutboundEntry[] }> {
  return request('/_api/outbound');
}

export type OutboundSlotInput = {
  credential: string;
  label: string;
  pricingMode: PricingMode;
  monthlyFeeCents?: number;
};

export async function createOutboundSlot(
  integrationId: IntegrationType,
  input: OutboundSlotInput,
): Promise<OutboundSlot> {
  return request(`/_api/outbound/${integrationId}`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateOutboundSlot(
  integrationId: IntegrationType,
  slot: number,
  input: OutboundSlotInput,
): Promise<OutboundSlot> {
  return request(`/_api/outbound/${integrationId}/${slot}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function deleteOutboundSlot(
  integrationId: IntegrationType,
  slot: number,
): Promise<{ id: string; slot: number; configured: false }> {
  return request(`/_api/outbound/${integrationId}/${slot}`, { method: 'DELETE' });
}

export async function listIntegrationTypes(): Promise<{ types: IntegrationTypeMeta[] }> {
  return request('/_api/inbound/types');
}

export async function listInbound(): Promise<{ inbound: InboundEntry[] }> {
  return request('/_api/inbound');
}

export async function createInbound(
  label: string,
  type: IntegrationType,
  outboundSlot: number,
): Promise<InboundCreated> {
  return request('/_api/inbound', {
    method: 'POST',
    body: JSON.stringify({ label, type, outboundSlot }),
  });
}

export async function revokeInbound(id: string): Promise<{ id: string; tokenPreview: string; revokedAt: string }> {
  return request(`/_api/inbound/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function pauseInbound(id: string): Promise<{ id: string; pausedAt: string }> {
  return request(`/_api/inbound/${encodeURIComponent(id)}/pause`, { method: 'POST' });
}

export async function resumeInbound(id: string): Promise<{ id: string; pausedAt: null }> {
  return request(`/_api/inbound/${encodeURIComponent(id)}/resume`, { method: 'POST' });
}

export async function listManagementKeys(): Promise<{ keys: ManagementKey[] }> {
  return request('/_api/mgmt-keys');
}

export async function createManagementKey(label: string): Promise<ManagementKeyCreated> {
  return request('/_api/mgmt-keys', {
    method: 'POST',
    body: JSON.stringify({ label }),
  });
}

export async function revokeManagementKey(id: string): Promise<{ id: string; revokedAt: string }> {
  return request(`/_api/mgmt-keys/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export type DailyUsage = {
  day: string;
  requests: number;
  inputTokens: number;
  outputTokens: number;
  errors: number;
  costMicros: number;
};

export async function fetchInboundDays(
  id: string,
): Promise<{ start: string; end: string; days: DailyUsage[] }> {
  return request(`/_api/inbound/${encodeURIComponent(id)}/days`);
}
